import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileText,
  X,
  Check,
  AlertTriangle,
  Loader2,
  FileSpreadsheet,
  Link2,
  Target,
  Type,
  Tag,
  Sparkles,
  RefreshCw,
  Copy,
  AlertCircle,
  CheckCircle2,
  Globe,
  ChevronDown,
  ChevronRight,
  Wand2,
  Shield,
  Zap,
  LayoutGrid,
  List,
  TrendingUp,
  Calendar,
  Plus,
  FolderPlus,
} from 'lucide-react';
import { fetchProjects, createProject } from '../api/projects';
import { bulkImportBacklinks, fetchBacklinks, checkDomainDuplicates } from '../api/backlinks';
import { fetchProviders, createProvider } from '../api/providers';
import { listSheets, parseGoogleSheets, checkDuplicates, validateUrls, detectColumns } from '../api/import';

// RFC4180-ish CSV parser. Handles quoted fields with embedded commas, doubled
// quotes ("" → "), CRLF/LF line endings, and trims trailing empty lines.
// Returns an array of arrays of strings.
function parseCSV(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else cell += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(cell); cell = ''; }
      else if (c === '\r') { /* swallow, handle on \n */ }
      else if (c === '\n') {
        row.push(cell);
        // skip rows that are entirely empty (e.g. trailing blank lines)
        if (row.some(v => v && v.trim())) rows.push(row);
        row = []; cell = '';
      } else cell += c;
    }
  }
  // last row (no trailing newline)
  if (cell !== '' || row.length) {
    row.push(cell);
    if (row.some(v => v && v.trim())) rows.push(row);
  }
  return rows;
}

// Auto-prepend https:// to URLs that look like a domain but are missing the
// scheme (e.g. "www.mercan.com/foo" → "https://www.mercan.com/foo"). Without
// this, strict URL parsing rejects scheme-less rows that are obviously valid.
function coerceHttpUrl(raw) {
  const s = (raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  // Looks like a host (starts with www. or has a dot before any whitespace)
  if (/^(www\.|[a-z0-9-]+\.[a-z]{2,})/i.test(s)) return `https://${s}`;
  return s;
}

// Column type icons and colors
const COLUMN_TYPES = {
  backlink_url: { icon: Link2, label: 'Backlink URL', color: 'text-info', bg: 'bg-info/20' },
  target_url: { icon: Target, label: 'Target URL', color: 'text-success', bg: 'bg-success/20' },
  expected_anchor: { icon: Type, label: 'Anchor Text', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  traffic: { icon: TrendingUp, label: 'Traffic', color: 'text-info', bg: 'bg-info/20' },
  month_of_order: { icon: Calendar, label: 'Month of Order', color: 'text-pink-400', bg: 'bg-pink-500/20' },
  year: { icon: Calendar, label: 'Year', color: 'text-primary', bg: 'bg-primary/20' },
  tags: { icon: Tag, label: 'Tags', color: 'text-warning', bg: 'bg-warning/20' },
  ignore: { icon: X, label: 'Ignore', color: 'text-muted-foreground', bg: 'bg-muted' },
};

function Import() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Core state
  const [activeTab, setActiveTab] = useState('sheets');
  const [projectId, setProjectId] = useState('');
  const [pasteData, setPasteData] = useState('');

  // Create project state
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDomain, setNewProjectDomain] = useState('');

  // Google Sheets state
  const [sheetsUrl, setSheetsUrl] = useState('');
  const [sheetsData, setSheetsData] = useState(null);
  const [columnMappings, setColumnMappings] = useState({});
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);

  // Multi-sheet tab state
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheetGid, setSelectedSheetGid] = useState(null);
  const [isLoadingTabs, setIsLoadingTabs] = useState(false);

  // Smart features state
  const [parsedRows, setParsedRows] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [urlValidations, setUrlValidations] = useState([]);
  const [bulkTags, setBulkTags] = useState('');
  const [showDuplicates, setShowDuplicates] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'domain'
  const [expandedDomains, setExpandedDomains] = useState(new Set());
  const [existingDomainCounts, setExistingDomainCounts] = useState({});

  // Provider / Batch state
  const [providerId, setProviderId] = useState('');
  const [batchLabel, setBatchLabel] = useState('');
  const [batchCost, setBatchCost] = useState('');
  const [showCreateProvider, setShowCreateProvider] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');

  // Progress state
  const [errors, setErrors] = useState([]);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [parseProgress, setParseProgress] = useState(0);

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
  });

  const { data: providersData } = useQuery({
    queryKey: ['providers'],
    queryFn: fetchProviders,
  });
  const providers = providersData?.providers || [];

  // Fetch existing backlinks for duplicate check
  const { data: existingBacklinks } = useQuery({
    queryKey: ['backlinks', projectId, 'all'],
    queryFn: () => fetchBacklinks({ project_id: projectId, limit: 10000, skip_history: true }),
    enabled: !!projectId,
  });

  const importMutation = useMutation({
    mutationFn: bulkImportBacklinks,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlinks']);
      const msg = data.skipped > 0
        ? `Imported ${data.count} backlinks (${data.skipped} duplicates skipped)`
        : `Successfully imported ${data.count} backlinks`;
      toast.success(msg);
      navigate(`/projects/${projectId}`);
    },
    onError: (error) => {
      const data = error.response?.data;
      if (data?.error === 'Validation failed' && Array.isArray(data?.details) && data.details.length) {
        const issuesShown = data.details.slice(0, 3).map(i =>
          `${(i.path || []).join('.') || 'field'}: ${i.message}`
        ).join('; ');
        const more = data.details.length > 3 ? ` (+${data.details.length - 3} more)` : '';
        toast.error(`Import validation failed — ${issuesShown}${more}`);
      } else {
        toast.error(data?.message || data?.error || 'Import failed');
      }
    },
  });

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: createProject,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['projects']);
      setProjectId(data.project.id);
      setShowCreateProject(false);
      setNewProjectName('');
      setNewProjectDomain('');
      toast.success(`Project "${data.project.name}" created!`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create project');
    },
  });

  const projects = projectsData?.projects || [];

  // Auto-detect domain from target URLs in parsed data
  const suggestedDomain = useMemo(() => {
    if (parsedRows.length === 0) return '';
    const firstTarget = parsedRows.find(row => row.target_url)?.target_url;
    if (!firstTarget) return '';
    try {
      return new URL(firstTarget).hostname;
    } catch {
      return '';
    }
  }, [parsedRows]);

  // Handle create project submission
  const handleCreateProject = () => {
    if (!newProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    if (!newProjectDomain.trim()) {
      toast.error('Please enter a target domain');
      return;
    }

    createProjectMutation.mutate({
      name: newProjectName.trim(),
      target_domain: newProjectDomain.trim().replace(/^https?:\/\//, '').replace(/\/$/, ''),
    });
  };

  // Handle project selection change
  const handleProjectChange = (e) => {
    const value = e.target.value;
    if (value === '__create_new__') {
      setShowCreateProject(true);
      // Auto-fill suggested domain if available
      if (suggestedDomain && !newProjectDomain) {
        setNewProjectDomain(suggestedDomain);
      }
    } else {
      setProjectId(value);
      setShowCreateProject(false);
    }
  };

  // Load a specific sheet tab's data
  const loadSheetData = async (gid) => {
    setIsLoadingSheets(true);
    try {
      const result = await parseGoogleSheets(sheetsUrl, undefined, gid);
      setSheetsData(result.data);

      // Auto-apply column suggestions
      const mappings = {};
      Object.entries(result.data.columnSuggestions || {}).forEach(([index, suggestion]) => {
        if (suggestion.confidence >= 0.5) {
          mappings[index] = suggestion.type;
        }
      });
      setColumnMappings(mappings);

      toast.success(`Loaded ${result.data.totalRows} rows from Google Sheets`);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to parse Google Sheets';
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Access denied. If this is an .xlsx file on Google Drive, convert it to a native Google Sheets format first.');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoadingSheets(false);
    }
  };

  // Parse Google Sheets URL — discover tabs first, then load data
  const handleParseSheets = async () => {
    if (!sheetsUrl.trim()) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }

    // Reset state
    setAvailableSheets([]);
    setSelectedSheetGid(null);
    setSheetsData(null);
    setColumnMappings({});
    setParsedRows([]);
    setIsLoadingTabs(true);

    try {
      const result = await listSheets(sheetsUrl);
      const sheets = result.sheets || [];
      setAvailableSheets(sheets);

      if (sheets.length === 1) {
        // Single sheet — load data directly
        setSelectedSheetGid(sheets[0].gid);
        await loadSheetData(sheets[0].gid);
      } else if (sheets.length > 1) {
        // Multiple sheets — show tab selector, auto-select first
        toast.success(`Found ${sheets.length} sheet tabs. Select one to load.`);
        setSelectedSheetGid(sheets[0].gid);
        await loadSheetData(sheets[0].gid);
      } else {
        // Fallback — try loading directly
        setSelectedSheetGid('0');
        await loadSheetData('0');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load sheet';
      if (error.response?.status === 403) {
        toast.error('Access denied. Make sure the sheet is public, and note that .xlsx files on Google Drive won\'t work — use a native Google Sheets file.');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoadingTabs(false);
    }
  };

  // Handle sheet tab click
  const handleSheetTabClick = async (gid) => {
    if (gid === selectedSheetGid && sheetsData) return; // Already loaded
    setSelectedSheetGid(gid);
    setSheetsData(null);
    setColumnMappings({});
    setParsedRows([]);
    await loadSheetData(gid);
  };

  // Process sheet data with current mappings
  const processSheetData = useCallback(() => {
    if (!sheetsData || !sheetsData.rows) return [];

    const backlinUrlCol = Object.entries(columnMappings).find(([_, type]) => type === 'backlink_url')?.[0];
    const targetUrlCol = Object.entries(columnMappings).find(([_, type]) => type === 'target_url')?.[0];
    const anchorCol = Object.entries(columnMappings).find(([_, type]) => type === 'expected_anchor')?.[0];
    const tagsCol = Object.entries(columnMappings).find(([_, type]) => type === 'tags')?.[0];
    const trafficCol = Object.entries(columnMappings).find(([_, type]) => type === 'traffic')?.[0];
    const monthCol = Object.entries(columnMappings).find(([_, type]) => type === 'month_of_order')?.[0];
    const yearCol = Object.entries(columnMappings).find(([_, type]) => type === 'year')?.[0];

    if (!backlinUrlCol || !targetUrlCol) return [];

    return sheetsData.rows
      .map((row, index) => {
        // Parse traffic value (remove commas, convert to number)
        let trafficValue = null;
        if (trafficCol) {
          const rawTraffic = row[parseInt(trafficCol)] || '';
          const parsed = parseInt(rawTraffic.replace(/,/g, ''), 10);
          if (!isNaN(parsed)) {
            trafficValue = parsed;
          }
        }

        // Parse year value
        let yearValue = null;
        if (yearCol) {
          const rawYear = row[parseInt(yearCol)] || '';
          const parsed = parseInt(rawYear, 10);
          if (!isNaN(parsed) && parsed >= 2000 && parsed <= 2100) {
            yearValue = parsed;
          }
        }

        return {
          index,
          backlink_url: coerceHttpUrl(row[parseInt(backlinUrlCol)] || ''),
          target_url: coerceHttpUrl(row[parseInt(targetUrlCol)] || ''),
          expected_anchor: anchorCol ? row[parseInt(anchorCol)] || '' : '',
          tags: tagsCol ? row[parseInt(tagsCol)] || '' : '',
          traffic: trafficValue,
          month_of_order: monthCol ? row[parseInt(monthCol)] || '' : '',
          year: yearValue,
        };
      })
      .filter(row => row.backlink_url && row.target_url);
  }, [sheetsData, columnMappings]);

  // Update parsed rows when mappings change
  useEffect(() => {
    if (activeTab === 'sheets' && sheetsData) {
      const processed = processSheetData();
      setParsedRows(processed);
    }
  }, [activeTab, sheetsData, columnMappings, processSheetData]);

  // Check for duplicates
  useEffect(() => {
    if (parsedRows.length > 0 && projectId && existingBacklinks?.backlinks) {
      const existingUrls = new Set(
        existingBacklinks.backlinks.map(b => normalizeUrl(b.backlink_url))
      );

      const urlCounts = new Map();
      const newDuplicates = [];

      parsedRows.forEach((row, index) => {
        const normalized = normalizeUrl(row.backlink_url);

        if (existingUrls.has(normalized)) {
          newDuplicates.push({
            index,
            type: 'existing',
            message: 'Already in project',
          });
        }

        if (urlCounts.has(normalized)) {
          newDuplicates.push({
            index,
            type: 'duplicate',
            message: `Duplicate of row ${urlCounts.get(normalized) + 1}`,
          });
        } else {
          urlCounts.set(normalized, index);
        }
      });

      setDuplicates(newDuplicates);
    }
  }, [parsedRows, projectId, existingBacklinks]);

  // Parse pasted data
  const parseData = useCallback((text) => {
    const lines = text.trim().split('\n').filter(line => line.trim());
    const parsed = [];
    const parseErrors = [];

    lines.forEach((line, index) => {
      const parts = line.split(/[,\t]/).map(p => p.trim());

      if (parts.length < 2) {
        parseErrors.push({ line: index + 1, message: 'Need at least backlink URL and target URL' });
        return;
      }

      const [rawBacklinkUrl, rawTargetUrl, expected_anchor] = parts;
      const backlink_url = coerceHttpUrl(rawBacklinkUrl);
      const target_url = coerceHttpUrl(rawTargetUrl);

      try {
        new URL(backlink_url);
        new URL(target_url);
        parsed.push({
          index,
          backlink_url,
          target_url,
          expected_anchor: expected_anchor || '',
        });
      } catch {
        parseErrors.push({ line: index + 1, message: 'Invalid URL format' });
      }
    });

    setParsedRows(parsed);
    setErrors(parseErrors);
  }, []);

  const handlePasteChange = (e) => {
    const text = e.target.value;
    setPasteData(text);
    if (text.trim()) {
      parseData(text);
    } else {
      setParsedRows([]);
      setErrors([]);
    }
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files (wrong format)
    if (rejectedFiles?.length > 0) {
      const rejected = rejectedFiles[0];
      const ext = rejected.file?.name?.split('.').pop()?.toLowerCase();
      if (ext === 'xlsx' || ext === 'xls') {
        toast.error('Excel files (.xlsx/.xls) are not supported. Please export as CSV first, or use Google Sheets import.');
      } else {
        toast.error(`Unsupported file format: .${ext}. Please use CSV or TXT files.`);
      }
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      // Double-check for xlsx/xls files that might slip through
      const ext = file.name?.split('.').pop()?.toLowerCase();
      if (ext === 'xlsx' || ext === 'xls') {
        toast.error('Excel files (.xlsx/.xls) are not supported. Please export as CSV first, or use Google Sheets import.');
        return;
      }

      setIsParsingFile(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        // Check if the content looks like binary/garbled data
        const nonPrintable = (text.match(/[\x00-\x08\x0E-\x1F]/g) || []).length;
        if (nonPrintable > text.length * 0.05) {
          toast.error('This file appears to be a binary format (possibly .xlsx). Please export as CSV first, or use Google Sheets import.');
          setIsParsingFile(false);
          return;
        }

        // Parse as a real CSV. If the file has a header row + multiple data
        // columns (the typical agency tracker shape), route through the smart
        // column mapper for the same UX as the Google Sheets path. If it looks
        // like a 2–3 col paste (backlink_url[, target_url[, anchor]]), fall
        // back to the simple paste parser.
        const allRows = parseCSV(text);
        const looksLikeMultiColumnSheet = allRows.length >= 2 && allRows[0].length >= 3
          && allRows[0].every(h => typeof h === 'string')
          && !/^https?:\/\//i.test(allRows[0][0] || '')   // header isn't a URL
          && !/^https?:\/\//i.test(allRows[0][1] || '');

        if (looksLikeMultiColumnSheet) {
          const headers = allRows[0].map(h => (h || '').trim());
          const dataRows = allRows.slice(1);
          // Reset paste state so the smart mapper takes over.
          setPasteData('');
          setParsedRows([]);
          setSheetsData(null);
          setColumnMappings({});
          // Switch to the sheets tab so the smart column mapping UI is visible.
          setActiveTab('sheets');
          try {
            const sample = dataRows.slice(0, 30);
            const detected = await detectColumns(headers, sample);
            const suggestions = detected?.suggestions || detected?.columnSuggestions || {};
            const mappings = {};
            Object.entries(suggestions).forEach(([index, suggestion]) => {
              if (suggestion?.confidence >= 0.5) mappings[index] = suggestion.type;
            });
            setSheetsData({
              headers,
              rows: dataRows,
              totalRows: dataRows.length,
              columnSuggestions: suggestions,
            });
            setColumnMappings(mappings);
            toast.success(`Loaded ${dataRows.length} rows from ${file.name}`);
          } catch (err) {
            // If column detection fails (network/auth), still show the rows so
            // the user can map columns manually.
            setSheetsData({ headers, rows: dataRows, totalRows: dataRows.length, columnSuggestions: {} });
            toast.error('Auto-detect failed — pick columns manually below');
          }
        } else {
          setPasteData(text);
          parseData(text);
        }
        setIsParsingFile(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setIsParsingFile(false);
      };
      reader.readAsText(file);
    }
  }, [parseData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
  });

  // Auto-fix URLs
  const handleAutoFix = async () => {
    const urls = parsedRows.map(r => r.backlink_url);
    try {
      const result = await validateUrls(urls);
      setUrlValidations(result.results);

      // Apply fixes to parsed rows
      const fixedRows = parsedRows.map((row, index) => {
        const validation = result.results.find(v => v.index === index);
        if (validation?.fixed) {
          return { ...row, backlink_url: validation.url };
        }
        return row;
      });

      setParsedRows(fixedRows);
      toast.success(`Fixed ${result.stats.fixed} URLs`);
    } catch (error) {
      toast.error('URL validation failed');
    }
  };

  // Get valid rows (excluding duplicates and rows with invalid URLs)
  const validRows = useMemo(() => {
    const isHttpUrl = (s) => {
      if (!s || typeof s !== 'string') return false;
      try {
        const u = new URL(s.trim());
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    };

    let rows = parsedRows.filter((_, index) =>
      !duplicates.some(d => d.index === index && (d.type === 'existing' || d.type === 'duplicate'))
    );

    // Reject rows where backlink_url or target_url isn't a valid http(s) URL —
    // these would cause the /api/backlinks/bulk endpoint to 400 the entire batch.
    rows = rows.filter(row => isHttpUrl(row.backlink_url) && isHttpUrl(row.target_url));

    // Apply bulk tags
    if (bulkTags.trim()) {
      rows = rows.map(row => ({
        ...row,
        tags: row.tags ? `${row.tags},${bulkTags}` : bulkTags,
      }));
    }

    return rows;
  }, [parsedRows, duplicates, showDuplicates, bulkTags]);

  // Count rows that had to be dropped due to bad URLs so we can warn the user.
  const invalidUrlRowCount = useMemo(() => {
    const isHttpUrl = (s) => {
      if (!s || typeof s !== 'string') return false;
      try {
        const u = new URL(s.trim());
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    };
    const nonDuplicateRows = parsedRows.filter((_, index) =>
      !duplicates.some(d => d.index === index && (d.type === 'existing' || d.type === 'duplicate'))
    );
    return nonDuplicateRows.filter(row => !(isHttpUrl(row.backlink_url) && isHttpUrl(row.target_url))).length;
  }, [parsedRows, duplicates]);

  // Group by domain for domain view
  const domainGroups = useMemo(() => {
    const groups = new Map();
    validRows.forEach(row => {
      try {
        const domain = new URL(row.backlink_url).hostname;
        if (!groups.has(domain)) {
          groups.set(domain, []);
        }
        groups.get(domain).push(row);
      } catch {
        // Skip invalid URLs
      }
    });
    return Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [validRows]);

  // Check for domain-level duplicates when domains change and a project is selected
  useEffect(() => {
    if (!projectId || domainGroups.length === 0) {
      setExistingDomainCounts({});
      return;
    }

    const domains = domainGroups.map(([domain]) => domain);
    checkDomainDuplicates(projectId, domains)
      .then(data => {
        setExistingDomainCounts(data.domainCounts || {});
      })
      .catch(() => {
        setExistingDomainCounts({});
      });
  }, [projectId, domainGroups]);

  const handleImport = () => {
    if (!projectId) {
      toast.error('Please select a project');
      return;
    }
    if (validRows.length === 0) {
      toast.error('No valid backlinks to import');
      return;
    }

    importMutation.mutate({
      project_id: projectId,
      provider_id: providerId || undefined,
      batch_label: batchLabel || undefined,
      batch_cost: batchCost ? parseFloat(batchCost) : undefined,
      backlinks: validRows.map(({ backlink_url, target_url, expected_anchor, traffic, month_of_order, year }) => ({
        backlink_url,
        target_url,
        expected_anchor,
        traffic: traffic || undefined,
        month_of_order: month_of_order || undefined,
        year: year || undefined,
      })),
    });
  };

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Smart Badge */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">Smart Import</h1>
            <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-500/20 to-primary/20 border border-purple-500/30 rounded-full text-xs font-medium text-purple-300">
              <Sparkles className="h-3 w-3" />
              AI-Powered
            </span>
          </div>
          <p className="text-muted-foreground mt-1">
            Import from Google Sheets, CSV, or paste URLs directly
          </p>
        </div>
      </div>

      {/* Project Selector */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px] max-w-md">
          <label className="block text-sm font-medium text-foreground mb-2">
            Select Project <span className="text-destructive">*</span>
          </label>
          <select
            value={showCreateProject ? '__create_new__' : projectId}
            onChange={handleProjectChange}
            className={`w-full px-4 py-2.5 bg-card border rounded-lg text-foreground focus:ring-2 focus:ring-ring ${
              !projectId && parsedRows.length > 0 && !showCreateProject
                ? 'border-warning ring-2 ring-warning/30'
                : 'border-border'
            }`}
          >
            <option value="">Choose a project...</option>
            <option value="__create_new__" className="text-success font-medium">
              + Create New Project
            </option>
            {projects.length > 0 && <option disabled>───────────────</option>}
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {!projectId && parsedRows.length > 0 && !showCreateProject && (
            <p className="text-xs text-warning mt-1">Required to enable import</p>
          )}
        </div>

        {/* Bulk Tags Input */}
        <div className="flex-1 min-w-[200px] max-w-md">
          <label className="block text-sm font-medium text-foreground mb-2">
            <Tag className="h-4 w-4 inline mr-1" />
            Bulk Tags (optional)
          </label>
          <input
            type="text"
            value={bulkTags}
            onChange={(e) => setBulkTags(e.target.value)}
            placeholder="guest-post, outreach, campaign-2024"
            className="w-full px-4 py-2.5 bg-card border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Provider / Batch Section */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px] max-w-xs">
          <label className="block text-sm font-medium text-foreground mb-2">
            Provider (optional)
          </label>
          <div className="flex gap-2">
            <select
              value={providerId}
              onChange={(e) => {
                if (e.target.value === '__create_new__') {
                  setShowCreateProvider(true);
                  setProviderId('');
                } else {
                  setProviderId(e.target.value);
                  setShowCreateProvider(false);
                }
              }}
              className="flex-1 px-4 py-2.5 bg-card border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">No provider</option>
              <option value="__create_new__">+ Create New</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {showCreateProvider && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={newProviderName}
                onChange={e => setNewProviderName(e.target.value)}
                placeholder="Provider name"
                className="flex-1 px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:ring-2 focus:ring-ring"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!newProviderName.trim()) return;
                  try {
                    const res = await createProvider({ name: newProviderName.trim() });
                    setProviderId(res.provider.id);
                    setShowCreateProvider(false);
                    setNewProviderName('');
                    queryClient.invalidateQueries({ queryKey: ['providers'] });
                    toast.success('Provider created');
                  } catch { toast.error('Failed to create provider'); }
                }}
                className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-[150px] max-w-xs">
          <label className="block text-sm font-medium text-foreground mb-2">
            Batch Label (optional)
          </label>
          <input
            type="text"
            value={batchLabel}
            onChange={e => setBatchLabel(e.target.value)}
            placeholder="e.g. Jan-2026 outreach"
            className="w-full px-4 py-2.5 bg-card border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="min-w-[120px] max-w-[160px]">
          <label className="block text-sm font-medium text-foreground mb-2">
            Batch Cost ($)
          </label>
          <input
            type="number"
            value={batchCost}
            onChange={e => setBatchCost(e.target.value)}
            placeholder="0.00"
            min="0"
            step="0.01"
            className="w-full px-4 py-2.5 bg-card border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Create New Project Form */}
      {showCreateProject && (
        <div className="p-5 bg-gradient-to-br from-success/10 to-success/5 border border-success/30 rounded-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success/20 rounded-lg">
              <FolderPlus className="h-5 w-5 text-success" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">Create New Project</h3>
              <p className="text-sm text-muted-foreground">Create a project to import your backlinks into</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Project Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="My SEO Campaign"
                className="w-full px-4 py-2.5 bg-card border border-border rounded-lg text-foreground focus:ring-2 focus:ring-success focus:border-success"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Target Domain <span className="text-destructive">*</span>
                {suggestedDomain && (
                  <button
                    type="button"
                    onClick={() => setNewProjectDomain(suggestedDomain)}
                    className="ml-2 text-xs text-success hover:text-success/80"
                  >
                    Use detected: {suggestedDomain}
                  </button>
                )}
              </label>
              <input
                type="text"
                value={newProjectDomain}
                onChange={(e) => setNewProjectDomain(e.target.value)}
                placeholder="example.com"
                className="w-full px-4 py-2.5 bg-card border border-border rounded-lg text-foreground focus:ring-2 focus:ring-success focus:border-success"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleCreateProject}
              disabled={createProjectMutation.isPending || !newProjectName.trim() || !newProjectDomain.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-success hover:bg-success/90 text-success-foreground rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createProjectMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {createProjectMutation.isPending ? 'Creating...' : 'Create Project'}
            </button>
            <button
              onClick={() => {
                setShowCreateProject(false);
                setNewProjectName('');
                setNewProjectDomain('');
              }}
              className="px-4 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('sheets')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
              activeTab === 'sheets'
                ? 'border-success text-success bg-success/10'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <FileSpreadsheet className="h-4 w-4" />
            Google Sheets
            <span className="px-1.5 py-0.5 bg-success/20 text-success text-xs rounded">
              NEW
            </span>
          </button>
          <button
            onClick={() => setActiveTab('paste')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
              activeTab === 'paste'
                ? 'border-primary text-primary bg-primary/10'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Type className="h-4 w-4" />
            Paste URLs
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
              activeTab === 'upload'
                ? 'border-primary text-primary bg-primary/10'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Upload className="h-4 w-4" />
            Upload CSV
          </button>
        </div>
      </div>

      {/* Google Sheets Tab */}
      {activeTab === 'sheets' && (
        <div className="space-y-6">
          {/* URL Input */}
          <div className="p-6 bg-gradient-to-br from-muted/50 to-card border border-border rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-success/20 rounded-lg">
                <FileSpreadsheet className="h-5 w-5 text-success" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Google Sheets URL</h3>
                <p className="text-sm text-muted-foreground">Paste your public Google Sheets link</p>
              </div>
            </div>

            <div className="flex gap-3">
              <input
                type="url"
                value={sheetsUrl}
                onChange={(e) => setSheetsUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-success focus:border-success"
              />
              <button
                onClick={handleParseSheets}
                disabled={isLoadingSheets || isLoadingTabs || !sheetsUrl.trim()}
                className="flex items-center gap-2 px-6 py-3 bg-success hover:bg-success/90 text-success-foreground rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingSheets || isLoadingTabs ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Zap className="h-5 w-5" />
                )}
                Load Sheet
              </button>
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              <Shield className="h-3 w-3 inline mr-1" />
              Make sure your sheet is set to "Anyone with the link can view"
            </p>
          </div>

          {/* Sheet Tab Selector */}
          {availableSheets.length > 1 && (
            <div className="flex items-center gap-1 p-1 bg-muted/50 border border-border rounded-lg overflow-x-auto">
              <FileSpreadsheet className="h-4 w-4 text-muted-foreground ml-2 mr-1 flex-shrink-0" />
              {availableSheets.map((sheet) => (
                <button
                  key={sheet.gid}
                  onClick={() => handleSheetTabClick(sheet.gid)}
                  disabled={isLoadingSheets}
                  className={`flex-shrink-0 px-3 py-1.5 text-sm rounded-md transition-all whitespace-nowrap ${
                    selectedSheetGid === sheet.gid
                      ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  } ${isLoadingSheets ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {sheet.name}
                  {isLoadingSheets && selectedSheetGid === sheet.gid && (
                    <Loader2 className="h-3 w-3 animate-spin inline ml-1.5" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Loading tabs indicator */}
          {isLoadingTabs && !sheetsData && (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Discovering sheet tabs...</span>
            </div>
          )}

          {/* Column Mapping */}
          {sheetsData && (
            <div className="p-6 bg-muted/50 border border-border rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Wand2 className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">Smart Column Mapping</h3>
                    <p className="text-sm text-muted-foreground">
                      {Object.keys(sheetsData.columnSuggestions || {}).length} columns auto-detected
                    </p>
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">
                  {sheetsData.totalRows} total rows
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {sheetsData.headers.map((header, index) => {
                  const suggestion = sheetsData.columnSuggestions?.[index];
                  const currentMapping = columnMappings[index];
                  const typeInfo = currentMapping ? COLUMN_TYPES[currentMapping] : null;

                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border transition-all ${
                        currentMapping && currentMapping !== 'ignore'
                          ? `${typeInfo?.bg} border-border`
                          : 'bg-card border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-muted-foreground truncate">
                          {header || `Column ${index + 1}`}
                        </span>
                        {suggestion && suggestion.confidence >= 0.7 && (
                          <Sparkles className="h-3 w-3 text-warning flex-shrink-0" />
                        )}
                      </div>

                      <select
                        value={currentMapping || ''}
                        onChange={(e) => {
                          setColumnMappings(prev => ({
                            ...prev,
                            [index]: e.target.value || undefined,
                          }));
                        }}
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded text-foreground"
                      >
                        <option value="">— Skip —</option>
                        {Object.entries(COLUMN_TYPES).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>

                      {suggestion && (
                        <p className="mt-1 text-xs text-muted-foreground truncate">
                          Sample: {suggestion.samples?.[0] || '—'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Paste Tab */}
      {activeTab === 'paste' && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Enter URLs (one per line)
          </label>
          <textarea
            value={pasteData}
            onChange={handlePasteChange}
            placeholder={`backlink_url, target_url, anchor_text (optional)\nhttps://example.com/blog, https://yoursite.com, Click here\nhttps://another.com/page, https://yoursite.com/landing`}
            rows={10}
            className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground font-mono text-sm focus:ring-2 focus:ring-ring resize-none"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Format: backlink_url, target_url, anchor_text (comma or tab separated)
          </p>
        </div>
      )}

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary bg-primary/10'
              : isParsingFile
              ? 'border-primary bg-primary/5 cursor-wait'
              : 'border-border hover:border-muted-foreground'
          }`}
        >
          <input {...getInputProps()} disabled={isParsingFile} />
          {isParsingFile ? (
            <>
              <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
              <p className="text-foreground mb-2">Processing file...</p>
            </>
          ) : (
            <>
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-foreground mb-2">
                {isDragActive ? 'Drop your file here' : 'Drag & drop a CSV file here'}
              </p>
              <p className="text-sm text-muted-foreground">or click to browse</p>
              <p className="text-xs text-muted-foreground mt-2">
                Supports .csv and .txt files. For Excel (.xlsx), export as CSV first or use Google Sheets import.
              </p>
            </>
          )}
        </div>
      )}

      {/* Smart Stats Bar */}
      {parsedRows.length > 0 && (
        <div className="flex flex-wrap gap-4 p-4 bg-muted/50 border border-border rounded-xl">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success" />
            <span className="text-sm text-foreground">
              <strong className="text-success">{validRows.length}</strong> valid
            </span>
          </div>

          {duplicates.filter(d => d.type === 'existing').length > 0 && (
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-warning" />
              <span className="text-sm text-foreground">
                <strong className="text-warning">
                  {duplicates.filter(d => d.type === 'existing').length}
                </strong> already exist
              </span>
            </div>
          )}

          {invalidUrlRowCount > 0 && (
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-error" />
              <span className="text-sm text-foreground">
                <strong className="text-error">{invalidUrlRowCount}</strong> dropped (invalid URL)
              </span>
            </div>
          )}

          {duplicates.filter(d => d.type === 'duplicate').length > 0 && (
            <div className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-warning" />
              <span className="text-sm text-foreground">
                <strong className="text-warning">
                  {duplicates.filter(d => d.type === 'duplicate').length}
                </strong> duplicates
              </span>
            </div>
          )}

          {errors.length > 0 && (
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <span className="text-sm text-foreground">
                <strong className="text-destructive">{errors.length}</strong> errors
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <Globe className="h-5 w-5 text-primary" />
            <span className="text-sm text-foreground">
              <strong className="text-primary">{domainGroups.length}</strong> domains
            </span>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 border border-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded ${viewMode === 'table' ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}
              title="Table View"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('domain')}
              className={`p-1.5 rounded ${viewMode === 'domain' ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}
              title="Domain View"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>

          {/* Auto-fix Button */}
          <button
            onClick={handleAutoFix}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 rounded-lg text-sm transition-colors"
          >
            <Wand2 className="h-4 w-4" />
            Auto-fix URLs
          </button>
        </div>
      )}

      {/* Preview Table */}
      {viewMode === 'table' && validRows.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium w-12">#</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium">Backlink URL</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium w-32">Anchor</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium w-24">Traffic</th>
                  <th className="text-left px-4 py-3 text-muted-foreground font-medium w-24">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {validRows.slice(0, 100).map((row, i) => {
                  const isDuplicate = duplicates.find(d => d.index === row.index);
                  return (
                    <tr
                      key={i}
                      className={`hover:bg-muted/50 ${
                        isDuplicate ? 'bg-warning/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3 text-muted-foreground">{i + 1}</td>
                      <td className="px-4 py-3 text-foreground truncate max-w-md font-mono text-xs">
                        {row.backlink_url}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground truncate">
                        {row.expected_anchor || '—'}
                      </td>
                      <td className="px-4 py-3 text-info text-right font-medium">
                        {row.traffic ? row.traffic.toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {isDuplicate ? (
                          <span className="flex items-center gap-1 text-warning text-xs">
                            <AlertCircle className="h-3 w-3" />
                            {isDuplicate.type === 'existing' ? 'Exists' : 'Dup'}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-success text-xs">
                            <Check className="h-3 w-3" />
                            Ready
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {validRows.length > 100 && (
            <div className="px-4 py-2 bg-muted/50 text-sm text-muted-foreground text-center">
              Showing first 100 of {validRows.length} rows
            </div>
          )}
        </div>
      )}

      {/* Domain View */}
      {viewMode === 'domain' && domainGroups.length > 0 && (
        <div className="space-y-2">
          {domainGroups.slice(0, 50).map(([domain, rows]) => (
            <div key={domain} className="border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleDomain(domain)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedDomains.has(domain) ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Globe className="h-4 w-4 text-primary" />
                  <span className="font-medium text-foreground">{domain}</span>
                </div>
                <div className="flex items-center gap-2">
                  {existingDomainCounts[domain.replace(/^www\./, '')] > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/20 text-warning">
                      <AlertTriangle className="h-3 w-3" />
                      {existingDomainCounts[domain.replace(/^www\./, '')]} already in project
                    </span>
                  )}
                  <span className="px-2 py-0.5 bg-muted rounded text-sm text-foreground">
                    {rows.length} {rows.length === 1 ? 'link' : 'links'}
                  </span>
                </div>
              </button>

              {expandedDomains.has(domain) && (
                <div className="divide-y divide-border">
                  {rows.map((row, i) => (
                    <div key={i} className="px-4 py-2 pl-12 text-sm">
                      <div className="text-foreground font-mono text-xs truncate">
                        {row.backlink_url}
                      </div>
                      {row.expected_anchor && (
                        <div className="text-muted-foreground text-xs mt-1">
                          Anchor: {row.expected_anchor}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {parsedRows.length === 0 && !sheetsData && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <FileText className="h-16 w-16 mb-4 opacity-30" />
          <p className="text-lg mb-2">No data to preview</p>
          <p className="text-sm">
            {activeTab === 'sheets'
              ? 'Enter a Google Sheets URL and click "Load Sheet"'
              : 'Paste URLs or upload a CSV file to get started'}
          </p>
        </div>
      )}

      {/* Import Button */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          {!projectId && validRows.length > 0 && (
            <span className="text-warning flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              Please select a project above to import
            </span>
          )}
          {projectId && validRows.length > 0 && (
            <>
              Ready to import <strong className="text-foreground">{validRows.length}</strong> backlinks
              {bulkTags && (
                <> with tags: <span className="text-purple-400">{bulkTags}</span></>
              )}
            </>
          )}
        </div>

        <button
          onClick={handleImport}
          disabled={!projectId || validRows.length === 0 || importMutation.isPending}
          className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-700 text-primary-foreground rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
        >
          {importMutation.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Sparkles className="h-5 w-5" />
          )}
          {importMutation.isPending
            ? `Importing ${validRows.length} Backlinks...`
            : `Import ${validRows.length} Backlinks`}
        </button>
      </div>
    </div>
  );
}

// Helper function to normalize URLs for comparison
function normalizeUrl(url) {
  try {
    const parsed = new URL(url.toLowerCase().trim());
    let normalized = parsed.origin + parsed.pathname;
    normalized = normalized.replace(/\/$/, '');
    normalized = normalized.replace(/^(https?:\/\/)www\./, '$1');
    return normalized;
  } catch {
    return url.toLowerCase().trim();
  }
}

export default Import;
